import { ButtonType } from '@tableau/widgets';
import * as React from 'react';
import _ = require('underscore');
import { ServiceLocator } from '../../app/ServiceLocator';
import { Formatters, Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { AngularContext, AngularContextFactory } from '../../ts/factories/AngularContextFactory';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { ISiteUserInfo } from '../../ts/models/ISiteUserInfo';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { Set } from '../../ts/models/Set';
import { SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteRolesResource } from '../services/resources/SiteRoles';
import { SiteUsers } from '../services/resources/SiteUsers';
import { IUpdateUsersAdminStatusResult } from '../../ts/ServerApi/requests/UpdateUsersAdminStatusRequest';
import { IError } from '../../ts/ServerApi/types/IError';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { ServerService } from '../../ts/services/ServerService';
import { ServerAdminUpdateActionNotification } from '../services/notifications/ServerAdminUpdateActionNotification';
import { SiteRolesUpdateActionNotification } from '../services/notifications/SiteRolesUpdateActionNotification';
import { SiteRolePopover } from './SiteRolePopover';

export interface SetSiteUsersRoleDialogState {
  readonly siteRole: string;
  readonly busy: boolean;
}

export interface SetSiteUsersRoleDialogProps {
  readonly close: () => void;
  readonly siteUsersToSet: Set<ISiteUserInfo>;
  readonly dismiss: () => void;
}

const SiteUserSiteRoleDropdownWidth = 260;

const SetSiteUsersRoleDialogStyle = {
  width: '400px'
};
export class SetSiteUsersRoleDialog extends React.Component<SetSiteUsersRoleDialogProps, SetSiteUsersRoleDialogState> {

  private dialogScope: ng.IScope = ServiceLocator.getAngularActionContext().scope;
  private angularContext: AngularContext = ServiceLocator.get<AngularContextFactory>('AngularContextFactory').create(this.dialogScope);

  private translate = this.angularContext.getService('$translate');
  private serverAdminUpdateActionNotification: ServerAdminUpdateActionNotification = ServerAdminUpdateActionNotification.instance;

  private $q: ng.IQService = this.angularContext.getService('$q');
  private server: ServerService = this.angularContext.getService('ServerService');
  private siteRoles: SiteRolesResource = SiteRolesResource.instance;
  private siteRolesOptionsResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  private siteRolesUpdateActionNotification: SiteRolesUpdateActionNotification = SiteRolesUpdateActionNotification.instance;

  private readonly translateCallback = (key: string) => {
    return this.translate.instant(key);
  }
  private siteRoleOptions = this.siteRoles.optionsForCurrentUser();

  private readonly onSiteRoleSelected = (roleOption: SiteRoleOption) => {
    this.setState({siteRole: roleOption.role});
  }

  public readonly setSiteRoleCallback = () => {
    this.setSiteUsersSiteRoles(this.props.siteUsersToSet, this.state.siteRole);
  }
  public setSiteUsersSiteRoles(selectedUsers: Set<IUserInfo>, siteRole: string): ng.IPromise<any> {
    this.setState({busy: true});
    const users = selectedUsers.clone();
    let setSiteUsersSiteRolesPromise: ng.IPromise<any>;
    if (siteRole === SiteRoles.ServerAdministrator) {
      setSiteUsersSiteRolesPromise = this.makeUsersServerAdminThenDisplayToast(users);
    } else {
      setSiteUsersSiteRolesPromise = this.demoteServerAdminsUpdateSiteRolesThenDisplayToasts(users, siteRole);
    }
    setSiteUsersSiteRolesPromise.finally(this.props.close);
    return setSiteUsersSiteRolesPromise;
  }

  public makeUsersServerAdminThenDisplayToast(users: Set<IUserInfo>): ng.IPromise<any> {
    return SiteUsers.instance.updateServerAdminStatusAndDelay(users.keys(), true)
      .then((result: IUpdateUsersAdminStatusResult) => {
        this.serverAdminUpdateActionNotification.notify(result, users, true);
      });
    }

  public demoteServerAdminsUpdateSiteRolesThenDisplayToasts(users: Set<IUserInfo>, siteRole: string): ng.IPromise<any> {
    const serverAdmins = users.filter((user: ISiteUserInfo) => user.siteRole === SiteRoles.ServerAdministrator);
    let showToastForDemotedAdmins = () => { };
    let usersWithoutErrors: Set<IUserInfo>;
    return this.demoteServerAdminsNoDelay(serverAdmins)
      .then((demoteServerAdminResult: IResult) => {
        usersWithoutErrors = this.getUsersWithoutErrorsInResults(demoteServerAdminResult, users);
        showToastForDemotedAdmins = (() => this.serverAdminUpdateActionNotification.notify(demoteServerAdminResult, serverAdmins, false));
        return SiteUsers.instance.updateUsersSiteRole(usersWithoutErrors.keys(), siteRole);
      })
      .then((updateUsersSiteRoleResult: IResult) => {
        showToastForDemotedAdmins();
        //if users were updated, show a toast for that too
        if (usersWithoutErrors.size() > 0) {
          this.siteRolesUpdateActionNotification.notify(updateUsersSiteRoleResult, usersWithoutErrors, siteRole);
        }
      });
  }

  public demoteServerAdminsNoDelay(serverAdmins: Set<IUserInfo>): ng.IPromise<IResult> {
    if (serverAdmins.size() === 0) {
      return this.$q.when({});
    } else if (!this.server.isSessionServerAdmin()) {
      return this.generateErrorsForUsersWithInsufficentPermissions(serverAdmins);
    } else {
      return SiteUsers.instance.updateServerAdminStatusNoDelay(serverAdmins.keys(), false);
    }
  }

  public generateErrorsForUsersWithInsufficentPermissions(serverAdmins: Set<IUserInfo>): ng.IPromise<IResult> {
    const result: IResult = { errors: [] };
    this.addErrorResultsForCallingUsersWithInsufficientPermissions(serverAdmins, result);
    return this.$q.when(result);
  }

  public addErrorResultsForCallingUsersWithInsufficientPermissions(serverAdmins: Set<IUserInfo>, result: IResult) {
    serverAdmins.each((user: ISiteUserInfo) => {
      result.errors.push({
        id: user.id,
        code: CommonErrorCode.CallingUserInsufficientPermissions
      });
    });
  }

  public getUsersWithoutErrorsInResults(result: IResult, users: Set<IUserInfo>): Set<IUserInfo> {
    let usersWithoutErrors = users.clone();
    if (result && result.errors) {
      _.each(result.errors, (error: IError) => {
        usersWithoutErrors.remove(usersWithoutErrors.get(error.id));
      });
    }
    return usersWithoutErrors;
  }

  public constructor(props: SetSiteUsersRoleDialogProps) {
    super(props);
    this.state = {
      siteRole: (this.props.siteUsersToSet.size() === 1) ? this.props.siteUsersToSet.first().siteRole : SiteRoles.Unlicensed,
      busy: false
    };
  }

  public render(){
    return (
      <div className="tb-set-site-users-role-modal" style={SetSiteUsersRoleDialogStyle}>
        <div className="tb-dialog-title">
          {Messages.confirmSetUsersSiteRoleTitle()}
        </div>
        <div className="tb-dialog-body-input-description">
          {this.getDialogDescription(this.props.siteUsersToSet.first().displayName, this.props.siteUsersToSet.size())}
        </div>
        <div className="tb-select-role-dropdown-container tb-row-with-icon-on-right tb-margin-top-wide">
        <SiteRolePickerMenu
              translate={this.translateCallback}
              siteRoleOptions={this.siteRoleOptions.map((siteRole: string) => this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole))}
              getSiteRoleOptionBySiteRole={(siteRole: string) => this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole)}
              onOptionSelected={this.onSiteRoleSelected}
              selectedRole={this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(this.state.siteRole)}
              isDisabled={this.state.busy}
              style={ {width: SiteUserSiteRoleDropdownWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize)} }
            />
          <div className="tb-row-icon-on-right">
            <SiteRolePopover angularContext={this.angularContext} />
          </div>
        </div>
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            showSpinner={false}
            clicked={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            title={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-margin-left-wide">
            <ButtonWithSpinner
              buttonType={ButtonType.Go}
              showSpinner={this.state.busy}
              clicked={this.setSiteRoleCallback}
              testId={'confirm-action-dialog-submit'}
              title={Messages.confirmSetUsersSiteRoleButton({
                ITEM_COUNT: this.props.siteUsersToSet.size(),
                ITEM_COUNT_LOC: Formatters.formatCount(this.props.siteUsersToSet.size())})}
              disabled={this.state.busy}
            />
          </span>
        </div>
      </div>
    );
  }

  private getDialogDescription(displayName: string, userCount: number) {
    return Messages.confirmSetUsersSiteRoleDesc(
      { USER_NAME: displayName,
      USER_COUNT: userCount,
      USER_COUNT_LOC: Formatters.formatCount(userCount)
    });
  }
}
